import { useShallow } from "zustand/react/shallow";
import ToggleSwitch from "@/components/ui/Switch";
import useGlobalStore from "@/store/globalStore";
import useNotificationStore, {
  type NotificationPreferenceGroup,
} from "../store/notificationStore";

/**
 * Notification preferences grouped the way people think about their work,
 * rather than as the 30 internal notification groups.
 *
 * Purely presentational: each toggle here maps to one or more server-side
 * groups (`groups`) and switches them together. A section only shows the
 * toggles whose groups this user can see (the server filters by permission),
 * and a group the map does not know yet lands in "Other" so nothing is hidden.
 */

interface PreferenceToggle {
  label: string;
  description: string;
  groups: string[];
}

interface PreferenceSection {
  title: string;
  description: string;
  toggles: PreferenceToggle[];
}

const SECTIONS: PreferenceSection[] = [
  {
    title: "Campaigns",
    description: "Activity on the campaigns you work on.",
    toggles: [
      {
        label: "Campaign changes",
        description: "A campaign is created, edited, briefed, deleted, or you are added to or removed from one.",
        groups: ["campaign"],
      },
      {
        label: "Flights",
        description: "Flights are created, changed, briefed or go live.",
        groups: ["flight"],
      },
      {
        label: "Assets",
        description: "Assets are added, changed or removed.",
        groups: ["asset"],
      },
      {
        label: "Creative progress",
        description: "Creative moves along: briefed, in progress, in review, approved, changes requested, live.",
        groups: ["asset_version"],
      },
      {
        label: "Comments on creative",
        description: "New comments, replies, likes and resolved comments on the canvas.",
        groups: ["canvas_comment"],
      },
    ],
  },
  {
    title: "Ad categories & specs",
    description: "Changes to the ad categories and specs campaigns are built on.",
    toggles: [
      {
        label: "Ad categories",
        description: "Ad categories change, including a new version your campaign needs to keep or update.",
        groups: ["ad_category"],
      },
      {
        label: "Publishers & ad types",
        description: "Publishers or ad types are archived or restored.",
        groups: ["publisher", "ad_type"],
      },
      {
        label: "Parameters",
        description: "Spec parameters (dimensions, file size, …) are added, changed or removed.",
        groups: ["parameter_definition", "custom_parameter"],
      },
      {
        label: "Ad templates",
        description: "Ad Builder templates and their media are saved.",
        groups: ["canvas_builder", "canvas_store"],
      },
    ],
  },
  {
    title: "Campaign setup lists",
    description: "The shared lists you pick from when setting up a campaign.",
    toggles: [
      {
        label: "Languages & objectives",
        description: "Languages or campaign objectives are added, changed or removed.",
        groups: ["language", "objective"],
      },
    ],
  },
  {
    title: "Team & people",
    description: "Who you work with and your own account.",
    toggles: [
      {
        label: "Teams",
        description: "Teams are created, changed or removed, or someone moves between teams.",
        groups: ["team"],
      },
      {
        label: "Team members",
        description: "People are added to, changed in or removed from a team.",
        groups: ["team_membership"],
      },
      {
        label: "Invitations to your agency",
        description: "Someone is invited to your agency, or accepts or declines.",
        groups: ["media_agency_invitation"],
      },
      {
        label: "Platform invitations",
        description: "Platform-wide invitations are sent, accepted or revoked.",
        groups: ["media_agency_global_invitation"],
      },
      {
        label: "Your account",
        description: "Sign-ups, profile, picture and password changes.",
        groups: ["user"],
      },
    ],
  },
  {
    title: "Roles & permissions",
    description: "Who can do what in MediaBridge.",
    toggles: [
      {
        label: "Roles & permissions",
        description: "Roles are created or changed, or their permissions are updated.",
        groups: ["role", "role_permission", "permission"],
      },
    ],
  },
  {
    title: "Agency & billing",
    description: "Your agency's details, security and payments.",
    toggles: [
      {
        label: "Agency profile & security",
        description: "Agency details, logo, security settings and account recovery.",
        groups: ["media_agency_information"],
      },
      {
        label: "Subscription & invoices",
        description: "Trials, plan changes, renewals, invoices and failed payments.",
        groups: ["subscription", "invoice"],
      },
      {
        label: "Addresses",
        description: "Billing or shipping addresses change.",
        groups: ["billing_address", "shipping_address"],
      },
    ],
  },
];

const NotificationPreferences = () => {
  const { preferences, isLoading, setGroupsEnabled } = useNotificationStore(
    useShallow((state) => ({
      preferences: state.preferences,
      isLoading: state.isLoading,
      setGroupsEnabled: state.setGroupsEnabled,
    })),
  );
  const { showSuccess, showError } = useGlobalStore(
    useShallow((state) => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    })),
  );

  const available = new Map<string, NotificationPreferenceGroup>(
    (preferences?.groups ?? []).map((group) => [group.group_key, group]),
  );

  // Keep only toggles the user can see; anything unmapped goes to "Other".
  const mapped = new Set(SECTIONS.flatMap((s) => s.toggles.flatMap((t) => t.groups)));
  const sections: PreferenceSection[] = [
    ...SECTIONS,
    {
      title: "Other",
      description: "Everything else.",
      toggles: Array.from(available.values())
        .filter((group) => !mapped.has(group.group_key))
        .map((group) => ({ label: group.display, description: "", groups: [group.group_key] })),
    },
  ]
    .map((section) => ({
      ...section,
      toggles: section.toggles
        .map((toggle) => ({ ...toggle, groups: toggle.groups.filter((key) => available.has(key)) }))
        .filter((toggle) => toggle.groups.length > 0),
    }))
    .filter((section) => section.toggles.length > 0);

  const handleToggle = async (toggle: PreferenceToggle, enabled: boolean) => {
    try {
      await setGroupsEnabled(toggle.groups, enabled);
      showSuccess(`${toggle.label} notifications turned ${enabled ? "on" : "off"}.`);
    } catch (error) {
      showError(
        error instanceof Error ? error.message : "Failed to save notification preferences",
      );
    }
  };

  if (isLoading && !preferences) {
    return <div className="py-10 text-center text-gray-500">Loading preferences...</div>;
  }

  if (sections.length === 0) {
    return (
      <div className="py-10 text-center text-gray-500">
        No notification preferences available.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {sections.map((section) => (
        <section key={section.title}>
          <h3 className="text-base font-semibold text-gray-900">{section.title}</h3>
          <p className="mb-3 text-sm text-gray-500">{section.description}</p>
          <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
            {section.toggles.map((toggle) => {
              // On when any of its groups is on: a partially enabled set still
              // delivers something, so showing it as "off" would mislead.
              const enabled = toggle.groups.some((key) => available.get(key)?.enabled);

              return (
                <div key={toggle.label} className="flex items-center justify-between gap-6 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{toggle.label}</p>
                    {toggle.description && (
                      <p className="text-sm text-gray-500">{toggle.description}</p>
                    )}
                  </div>
                  <ToggleSwitch
                    enabled={enabled}
                    setEnabled={() => handleToggle(toggle, !enabled)}
                  />
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default NotificationPreferences;
